import { supabase } from '@/integrations/supabase/client';
import {
  PriceForecastModel,
  CreatePriceForecastModelInput,
  PriceForecast,
  CreatePriceForecastInput,
  PriceForecastFilters,
  BuySignal,
  CreateBuySignalInput,
  BuySignalFilters,
  PreparedForecastData,
} from '@/types/database/price-forecast';

export const priceForecastService = {
  // Forecast Models
  async getModels(productName?: string, locationGroup?: string) {
    let query = supabase
      .from('price_forecast_models')
      .select('*')
      .eq('is_active', true)
      .order('last_trained_at', { ascending: false });

    if (productName) {
      query = query.eq('product_name', productName);
    }

    if (locationGroup) {
      query = query.eq('location_group', locationGroup);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching forecast models:', error);
      throw error;
    }

    return data as PriceForecastModel[];
  },

  async getModel(id: string) {
    const { data, error } = await supabase
      .from('price_forecast_models')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching forecast model:', error);
      throw error;
    }

    return data as PriceForecastModel;
  },

  // Price Forecasts
  async getForecasts(filters: PriceForecastFilters = {}) {
    let query = supabase
      .from('price_forecasts')
      .select('*')
      .order('forecast_date', { ascending: true });

    if (filters.product_name) {
      query = query.eq('product_name', filters.product_name);
    }

    if (filters.location_group) {
      query = query.eq('location_group', filters.location_group);
    }

    if (filters.date_from) {
      query = query.gte('forecast_date', filters.date_from);
    }

    if (filters.date_to) {
      query = query.lte('forecast_date', filters.date_to);
    }

    if (filters.model_id) {
      query = query.eq('model_id', filters.model_id);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching price forecasts:', error);
      throw error;
    }

    return data as PriceForecast[];
  },

  async updateActualPrice(id: string, actualPrice: number) {
    const forecast = await supabase
      .from('price_forecasts')
      .select('predicted_price')
      .eq('id', id)
      .single();

    if (forecast.error) throw forecast.error;

    const predictionError = actualPrice - forecast.data.predicted_price;

    const { data, error } = await supabase
      .from('price_forecasts')
      .update({
        actual_price: actualPrice,
        prediction_error: predictionError,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating actual price:', error);
      throw error;
    }

    return data as PriceForecast;
  },

  // Buy Signals
  async getBuySignals(filters: BuySignalFilters = {}) {
    let query = supabase
      .from('buy_signals')
      .select('*')
      .order('signal_date', { ascending: true });

    if (filters.product_name) {
      query = query.eq('product_name', filters.product_name);
    }

    if (filters.status) {
      query = query.eq('status', filters.status);
    } else {
      // Default to active signals only
      query = query.eq('status', 'active');
    }

    if (filters.date_from) {
      query = query.gte('signal_date', filters.date_from);
    }

    if (filters.date_to) {
      query = query.lte('signal_date', filters.date_to);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching buy signals:', error);
      throw error;
    }

    return data as BuySignal[];
  },

  async updateBuySignalStatus(id: string, status: 'active' | 'executed' | 'expired') {
    const { data, error } = await supabase
      .from('buy_signals')
      .update({ status })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating buy signal status:', error);
      throw error;
    }

    return data as BuySignal;
  },

  // Edge Function Calls
  async prepareForecastData(
    productName: string,
    locationGroups: string[],
    startDate: string,
    endDate: string
  ): Promise<PreparedForecastData> {
    const { data, error } = await supabase.functions.invoke('prepare-forecast-data', {
      body: {
        product_name: productName,
        location_groups: locationGroups,
        start_date: startDate,
        end_date: endDate,
      },
    });

    if (error) {
      console.error('Error preparing forecast data:', error);
      throw error;
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to prepare forecast data');
    }

    return data as PreparedForecastData;
  },

  // Train a forecast model
  async trainModel(
    productName: string,
    locationGroups: string[],
    startDate: string,
    endDate: string
  ) {
    const { data, error } = await supabase.functions.invoke('train-forecast-model', {
      body: { 
        product_name: productName, 
        location_groups: locationGroups, 
        start_date: startDate, 
        end_date: endDate 
      },
    });
    
    if (error) {
      console.error('Error training model:', error);
      throw error;
    }
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to train model');
    }
    
    return data;
  },

  // Generate price forecasts
  async generateForecasts(
    productName: string,
    forecastHorizon: number,
    locationGroups?: string[]
  ) {
    const { data, error } = await supabase.functions.invoke('generate-price-forecast', {
      body: { 
        product_name: productName, 
        forecast_horizon: forecastHorizon, 
        location_groups: locationGroups 
      },
    });
    
    if (error) {
      console.error('Error generating forecasts:', error);
      throw error;
    }
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to generate forecasts');
    }
    
    return data;
  },

  // Generate buy signals
  async generateBuySignals(
    productName?: string,
    options?: {
      forecastHorizon?: number;
      minPriceAdvantage?: number;
      minConfidence?: number;
    }
  ) {
    const { data, error } = await supabase.functions.invoke('generate-buy-signals', {
      body: { 
        product_name: productName,
        forecast_horizon: options?.forecastHorizon,
        min_price_advantage: options?.minPriceAdvantage,
        min_confidence: options?.minConfidence,
      },
    });
    
    if (error) {
      console.error('Error generating buy signals:', error);
      throw error;
    }
    
    if (!data.success) {
      throw new Error(data.error || 'Failed to generate buy signals');
    }
    
    return data;
  },
};
